/* eslint-disable react/display-name */
/**
 * Search Bar
 * @author Nicolas Venne
 *
 * @copyright Klipfolio, All rights reserved
 */

import React, { useRef, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Icon from '@icons'
import Text from '@particles/text'
import { useStaticQuery, graphql } from 'gatsby'
import useIsMounted from '@hooks/use-is-mounted'
import { navigate } from 'gatsby'
import scrollIntoView from 'scroll-into-view-if-needed'
import {
  InstantSearch,
  connectSearchBox,
  connectHits,
  connectStateResults,
  connectHighlight,
  connectRefinementList,
  Configure,
  Index
} from 'react-instantsearch-dom'
import { useComponentVisible, useFocus } from '@particles/hooks'
import Layout from '@organisms/layout'
import { Link } from 'gatsby'
import ServiceLogo from '@atoms/service-logo'
import Heading from '@particles/heading'
import Formula from '@atoms/formula'
import Line from '@particles/line'
import Tags from '@molecules/tags'
import Button from '@atoms/button'
import { Defaults } from '@styles/theme'
import { media, useMedia } from '@breakpoints'
import algoliasearch from 'algoliasearch/lite'
import { useCookies } from 'react-cookie'
import Contributor from '@molecules/contributor'

const StyledButton = styled(Button)`
  background: #75e1b2;
  color: #014c33;
  border-radius: 2rem;
  padding: 0.5rem 1rem;
  font-size: 1.1rem;
`

const HotkeyContainer = styled.div`
  display: flex;
  gap: 0.25rem;
  opacity: 0.4;
  align-self: center;
`

const StyledHotkey = styled.span`
  background: linear-gradient(
    83.42deg,
    rgba(255, 255, 255, 0.15) 14.53%,
    rgba(255, 255, 255, 0.35) 243.09%
  );
  border-radius: 0.5rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.7rem;
  color: white;
`

const algoliaClient = algoliasearch(
  'U276KG3JBP',
  'cf30bf2b0d0511db02921358e65eeeaa',
  { _useRequestCache: true }
)

const searchClient = {
  search(requests) {
    const shouldSearch = requests.some(({ params: { query } }) => query !== '')
    if (shouldSearch) {
      return algoliaClient.search(requests)
    }
    return Promise.resolve({
      results: [{ hits: [] }]
    })
  },
  searchForFacetValues(requests) {
    const shouldSearch = requests.some(({ params: { query } }) => query !== '')
    if (shouldSearch) {
      return algoliaClient.searchForFacetValues(requests)
    }
    return Promise.resolve({
      results: [{ hits: [] }]
    })
  }
}

/**
 * Enum PREVIEWS
 * One for each specific index
 * Used for preview
 */
const PREVIEWS = {
  Metric: 'metric',
  Comparison: 'comparison',
  Services: 'service',
  Categories: 'category',
  Roles: 'role'
}

function updateQueryNoReload(query) {
  let url = null
  if (query !== null) {
    url = new URL(window.location)
    if (query) {
      url.searchParams.set('q', query)
    } else {
      url.searchParams.delete('q')
    }
    window.history.replaceState({}, '', url)
  }
}

const SearchNavButton = styled.button`
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
  display: flex;
  padding: 0;
  text-align: center;

  ${(props) =>
    !props.mobile &&
    css`
      background: linear-gradient(
        90deg,
        rgba(0, 0, 0, 0.15) 4.55%,
        rgba(0, 0, 0, 0.35) 132.5%
      );
      border-radius: 2rem;
      padding: 0.5rem 1rem;
    `}
`

// Button to open search
const SearchBarButton = styled.button`
  padding: 1rem 1.5rem;
  border-radius: 2rem;
  border: none;
  outline: none;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 0.2s;
  transition-property: all;
  background-color: white;
  display: flex;
  align-items: center;
  font-size: 1rem;
  text-align: left;
  width: 100vw;
  box-shadow: 0px 4px 40px rgba(0, 0, 0, 0.25);
  max-width: 55rem;
  ${media.desktop_medium`
        max-width: 45rem;
  `}
  ${media.desktop_small`
        max-width: 35rem;
  `}
  ${media.phablet`
    max-width: 25rem;
    width: 100%;
  `};
  color: white;
  background: rgba(255, 255, 255, 0.2);

  :hover {
    cursor: pointer;
    box-shadow: 0 4px 11px -2px rgb(37 44 97 / 15%),
      0 1px 3px 0 rgb(93 100 148 / 30%);
  }
`

const SearchContainerOverlay = styled.div`
  position: fixed;
  padding: 3rem;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  background-color: rgba(182, 195, 183, 0.5);
  display: flex;
  backdrop-filter: blur(4px);
  justify-content: center;
  align-items: flex-start;

  ${media.desktop`
    padding: 0;
  `}
`

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: ${Defaults.borderRadius};
  box-shadow: 0 8px 22px 0 rgb(37 44 97 / 15%),
    0 4px 6px 0 rgb(93 100 148 / 20%);
  width: 100%;
  max-width: 1024px;
  ${media.desktop`
    max-width: 100vw;
  `}

  ${({ hasQuery }) =>
    hasQuery
      ? css`
          max-height: 832px;
          height: 100%;
        `
      : undefined}

  justify-content: space-between;
  overflow: hidden;

  ${media.desktop`
    border-radius: 0;
    max-height: 100%;
  `}
`

const SearchHeader = styled.div`
  display: flex;
  flex: none;
  height: 4rem;
  align-items: center;
`

const StyledSearchForm = styled.form`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`

const StyledSearchInput = styled.input`
  appearance: none;
  outline: none;
  border: none;
  font-size: 1.25rem;
  width: 100%;
  padding: 0;
  color: ${({ theme }) => theme.palette.text['200']};
  font-family: 'Inter', Arial, sans-serif;
`

const StyledResetButton = styled.button`
  outline: none;
  background: none;
  border: none;
  height: 100%;
  padding: 0 1.5rem;
  box-sizing: border-box;
  cursor: pointer;

  :focus {
    outline: 5px auto -webkit-focus-ring-color;
  }
`

const SearchFooter = styled.footer`
  padding: 1rem;
  display: flex;
  border-top: 1px solid #11111122;
  align-items: center;
`

const TextButton = styled.button`
  outline: none;
  border: none;
  background: none;
  font-family: 'Inter', Arial, sans-serif;
  color: ${({ theme }) => theme.palette.text['200']};

  :hover {
    color: ${({ theme }) => theme.palette.text['300']};
  }
  :focus {
    outline: 5px auto -webkit-focus-ring-color;
  }

  height: 100%;
  padding: 0 1rem;
  cursor: pointer;
`

// const IndexHeader = styled.div`
//   padding: 0.25rem 1rem;
//   margin: 1rem 0;

//   :first-child {
//     margin-top: 0;
//   }
//   background: ${({ theme }) => theme.palette.background['200']};
//   ${Text} {
//     text-transform: uppercase;
//     letter-spacing: 0.06rem;
//     color: #aaa;
//   }
// `
const StyledPreviewBox = styled.div`
  display: flex;
  flex: none;
  width: 65%;
  box-shadow: inset 1px 1px 2px rgb(148 150 160 / 24%),
    inset 2px 2px 8px rgb(148 150 160 / 10%);
`

const PreviewBox = React.memo(({ style, onPreviewRender }) => {
  const [preview, setPreview] = useState(null)
  const isMounted = useIsMounted()

  const setPreviewCallback = useCallback(
    (data) => {
      if (isMounted.current) {
        setPreview(data)
      }
    },
    [setPreview]
  )

  useEffect(() => {
    onPreviewRender(setPreviewCallback)
  }, [setPreviewCallback])

  return (
    <StyledPreviewBox style={style}>
      {preview && preview.type === PREVIEWS.Metric && (
        <MetricPreview metric={preview.data} />
      )}
      {preview && preview.type === PREVIEWS.Comparison && (
        <ComparePreview compare={preview.data} />
      )}
      {preview && preview.type === PREVIEWS.Services && (
        <ServicePreview service={preview.data} />
      )}
      {preview && preview.type === PREVIEWS.Categories && (
        <CategoryPreview category={preview.data} />
      )}
      {preview && preview.type === PREVIEWS.Roles && (
        <RolePreview role={preview.data} />
      )}
    </StyledPreviewBox>
  )
})

PreviewBox.propTypes = {
  style: PropTypes.object,
  onPreviewRender: PropTypes.func
}

const SearchResultBox = styled.div`
  overflow-y: auto;
  width: 35%;
  -webkit-overflow-scrolling: touch;
  position: relative;

  ${media.desktop`
    width: 100%;
    overflow-y: scroll;
  `}
`

const SearchList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const HitItemLink = styled(Link)`
  height: 3.5rem;
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 0 1rem;
  font-size: 0.9rem;
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
  transition-duration: 75ms;
  transition-property: background-color, border-color, color, fill, stroke;
  ${Icon} {
    fill: ${({ theme }) => theme.palette.background['300']};
  }
  ${({ selected }) => {
    return selected
      ? css`
          background: ${({ theme }) => theme.colors.primary};
          box-shadow: 0 4px 11px 0 rgb(37 44 97 / 15%),
            0 1px 3px 0 rgb(93 100 148 / 20%);
          ${Text} {
            pointer-events: none;
            color: white !important;
            mark {
              color: white !important;
            }
          }
          ${Icon} {
            fill: white !important;
            pointer-events: none;
          }
        `
      : undefined
  }}
`
/**
 * Algolia Custom highlight component to highlight search results.
 */
const Highlight = ({ highlight, attribute, hit, as, margin }) => {
  const parsedHit = highlight({
    highlightProperty: '_highlightResult',
    attribute,
    hit
  })

  return (
    <Text as={as || 'span'} margin={margin} style={{ flex: 1 }}>
      {parsedHit.map((part, index) =>
        part.isHighlighted ? <mark key={index}>{part.value}</mark> : part.value
      )}
    </Text>
  )
}

Highlight.propTypes = {
  highlight: PropTypes.any,
  attribute: PropTypes.any,
  hit: PropTypes.any,
  as: PropTypes.string,
  margin: PropTypes.string
}

const ItemLinkHighlight = connectHighlight(Highlight)

const ItemLinkPropTypes = {
  index: PropTypes.string,
  data: PropTypes.any,
  to: PropTypes.string,
  type: PropTypes.string,
  select: PropTypes.func,
  passSetSelected: PropTypes.func
}

// List item components for every index
const ItemLink = React.memo(
  ({ type, passSetSelected, data, select, index, to }) => {
    const ref = useRef(null)
    const isMounted = useIsMounted()
    const [selected, setSelected] = useState(false)

    const setSelectedCallback = useCallback(
      (selected) => {
        if (isMounted.current) {
          setSelected(selected)
        }
      },
      [setSelected]
    )

    const selectCallback = useCallback(() => {
      isMounted.current && select(index, { type, data })
    }, [index])

    useEffect(() => {
      passSetSelected(setSelectedCallback, index)
    }, [setSelectedCallback, index])

    useEffect(() => {
      if (selected) {
        if (ref.current) {
          scrollIntoView(ref.current, {
            scrollMode: 'if-needed',
            block: 'nearest',
            inline: 'nearest'
          })
        }
        selectCallback()
      }
    }, [selected])

    return (
      <HitItemLink
        ref={ref}
        onMouseEnter={() => {
          setSelectedCallback(true)
        }}
        onFocus={() => {
          setSelectedCallback(true)
        }}
        to={to}
        selected={selected}
      >
        {type === PREVIEWS.Metric && (
          <Icon margin="R1" size={1.75} name="metric" />
        )}
        {type === PREVIEWS.Comparison && (
          <Icon margin="R1" size={1.75} name="compare" />
        )}
        {type === PREVIEWS.Services && (
          <ServiceLogo logo={data.name} xs margin="R1" />
        )}
        {type === PREVIEWS.Categories && (
          <Icon margin="R1" size={1.75} name="category" />
        )}
        {type === PREVIEWS.Roles && (
          <Icon margin="R1" size={1.75} name="role" />
        )}
        <ItemLinkHighlight hit={data} attribute="name" />
      </HitItemLink>
    )
  }
)

ItemLink.propTypes = ItemLinkPropTypes

// Preview components for every index
const MetricPreview = ({ metric }) => (
  <Layout.Column style={{ width: '100%' }} center padding="1.5 3">
    <StyledIcon>
      <Icon size={2} name="metric" style={{ minHeight: '2rem' }} />
    </StyledIcon>
    <Heading h3 margin="T1">
      {metric.name}
    </Heading>
    <ItemLinkHighlight
      as="p"
      margin="T2"
      hit={metric}
      attribute="description"
    />
    {metric.formula?.map(({ formula }, i) => (
      <Formula key={`formula-${i}`}>{formula}</Formula>
    ))}
    {metric.roles.length > 0 && (
      <Layout.Column style={{ width: '100%' }}>
        <Text as="span" size={0.7} uppercase spacing={0.05} bold>
          Roles
        </Text>
        <Line style={{ width: '100%' }} margin="T0.25 B1" />
        <Tags
          wrap="left"
          solid="true"
          tags={metric.roles.map((role) => ({
            name: role.name,
            link: role.url
          }))}
        />
      </Layout.Column>
    )}
    {metric.categories.length > 0 && (
      <Layout.Column style={{ width: '100%' }} margin="T2">
        <Text as="span" size={0.7} uppercase spacing={0.05} bold>
          Categories
        </Text>
        <Line style={{ width: '100%' }} margin="T0.25 B1" />
        <Tags
          wrap="left"
          solid="true"
          tags={metric.categories.map((cat) => ({
            name: cat.name,
            link: cat.url
          }))}
        />
      </Layout.Column>
    )}
    {metric.services.length > 0 && (
      <Layout.Column style={{ width: '100%' }} margin={'T2'}>
        <Text as="span" size={0.7} uppercase spacing={0.05} bold>
          Services
        </Text>
        <Line style={{ width: '100%' }} margin="T0.25 B1" />
        <Layout.Row>
          {metric.services.map((service, i) => {
            return (
              <Link
                key={`metric-service-${i}`}
                to={`/services/${service.name
                  .toLowerCase()
                  .replace(/ /g, '-')}`}
              >
                <ServiceLogo margin="0 0.5" sm logo={service.name} />
              </Link>
            )
          })}
        </Layout.Row>
      </Layout.Column>
    )}
    {metric.contributors?.length > 0 && (
      <Layout.Column style={{ width: '100%' }} margin={'T2'} padding={'B2'}>
        <Text as="span" size={0.7} uppercase spacing={0.05} bold>
          Contributors
        </Text>
        <Line style={{ width: '100%' }} margin={'T0.25 B1'} />
        <Layout.Row>
          {metric.contributors.map((contrib, i) => {
            return (
              <Contributor
                key={`contributor-preview-${i}`}
                name={contrib.name}
                web={`/contributor/${contrib.name
                  .toLowerCase()
                  .replace(/ /g, '_')}`}
                imageFile={{ cdn: contrib.contribImage.file }}
                imageAlt={contrib.contribImage.alt}
                forSearch
              />
            )
          })}
        </Layout.Row>
      </Layout.Column>
    )}
    <Button margin="T1.5" link={metric.url}>
      Learn more
    </Button>
    <Layout.Column style={{ minHeight: '3rem', width: '100%' }} />
  </Layout.Column>
)

MetricPreview.propTypes = {
  metric: PropTypes.any
}

const ComparePreview = ({ compare }) => (
  <Layout.Column style={{ width: '100%' }} center padding="1.5 3">
    <StyledIcon>
      <Icon size={2} name="compare" style={{ minHeight: '2rem' }} />
    </StyledIcon>
    <Heading h3 margin="T2">
      {compare.name}
    </Heading>
    <Line margin="T2 B2" style={{ width: '100%' }} />
    <Heading h4>{compare.metricA.name}</Heading>
    <ItemLinkHighlight
      as="p"
      margin="T2"
      hit={compare}
      attribute="metricA.description"
    />
    <Line margin="T2 B2" style={{ width: '100%' }} />

    <Heading h4>{compare.metricB.name}</Heading>
    <ItemLinkHighlight
      as="p"
      margin="T2"
      hit={compare}
      attribute="metricB.description"
    />
    <Button margin="T1.5" link={compare.url}>
      Learn More
    </Button>
    <Layout.Column style={{ minHeight: '3rem', width: '100%' }} />
  </Layout.Column>
)

ComparePreview.propTypes = {
  compare: PropTypes.any
}

const ServicePreview = ({ service }) => (
  <Layout.Column style={{ width: '100%' }} center padding="1.5 3">
    <StyledServiceLogo logo={service.name} sm />
    <Heading h3 margin="T1">
      {service.name}
    </Heading>
    {service.related && (
      <Layout.Column style={{ width: '100%' }} margin="T2" padding="B2">
        <Text as="span" size={0.7} uppercase spacing={0.05} bold>
          Top Metrics
        </Text>
        <Line style={{ width: '100%' }} margin="T0.25 B1" />
        <Tags
          wrap="left"
          fill="100%"
          solid="true"
          tags={service.related.slice(0, 5).map((metric) => ({
            name: metric.name,
            link: metric.url
          }))}
        />
      </Layout.Column>
    )}
    <Button margin="T1.5 B3" link={service.url}>
      View more
    </Button>
  </Layout.Column>
)

ServicePreview.propTypes = {
  service: PropTypes.any
}

const StyledServiceLogo = styled(ServiceLogo)`
  background: white;
  padding: 1rem;
  border-radius: 50%;
  filter: drop-shadow(0px 4px 32px rgba(0, 0, 0, 0.15));
`

const StyledIcon = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 50%;
  filter: drop-shadow(0px 4px 32px rgba(0, 0, 0, 0.15));
`

const CategoryPreview = ({ category }) => (
  <Layout.Column style={{ width: '100%' }} center padding="1.5 3">
    <StyledIcon>
      <Icon size={2} name="category" style={{ minHeight: '2rem' }} />
    </StyledIcon>
    <Heading h3 margin="T1">
      {category.name}
    </Heading>
    {category.related && (
      <Layout.Column style={{ width: '100%' }} margin="T2" padding="B2">
        <Text as="span" size={0.7} uppercase spacing={0.05} bold>
          Top Metrics
        </Text>
        <Line style={{ width: '100%' }} margin="T0.25 B1" />
        <Tags
          wrap="left"
          fill="100%"
          solid="true"
          tags={category.related.slice(0, 5).map((metric) => ({
            name: metric.name,
            link: metric.url
          }))}
        />
      </Layout.Column>
    )}
    <Button margin="T1.5 B3" link={category.url}>
      View more
    </Button>
  </Layout.Column>
)

CategoryPreview.propTypes = {
  category: PropTypes.any
}

const RolePreview = ({ role }) => (
  <Layout.Column style={{ width: '100%' }} center padding="1.5 3">
    <StyledIcon>
      <Icon size={2} name="role" style={{ minHeight: '2rem' }} />
    </StyledIcon>
    <Heading h3 margin="T1">
      {role.name}
    </Heading>
    {role.related && (
      <Layout.Column style={{ width: '100%' }} margin="T2" padding="B2">
        <Text as="span" size={0.7} uppercase spacing={0.05} bold>
          Top Metrics
        </Text>
        <Line style={{ width: '100%' }} margin="T0.25 B1" />
        <Tags
          wrap="left"
          fill="100%"
          solid="true"
          tags={role.related.slice(0, 5).map((metric) => ({
            name: metric.name,
            link: metric.url
          }))}
        />
      </Layout.Column>
    )}
    <Button margin="T1.5 B3" link={role.url}>
      View more
    </Button>
  </Layout.Column>
)

RolePreview.propTypes = {
  role: PropTypes.any
}

const HitsPropTypes = {
  hits: PropTypes.any,
  select: PropTypes.func,
  passSetSelected: PropTypes.func
}

// Algolia custom hits component for every index
const CustomHits = ({ hits, select, passSetSelected }) => {
  return (
    <SearchList>
      {hits.map((hit, i) => {
        return (
          <li key={hit.objectID}>
            <ItemLink
              key={hit.objectID + '-link'}
              to={hit.url}
              type={hit.type}
              index={i}
              data={hit}
              select={select}
              passSetSelected={passSetSelected}
            />
          </li>
        )
      })}
    </SearchList>
  )
}
CustomHits.propTypes = HitsPropTypes

const Hits = connectHits(CustomHits)

// Algolia custom state result for index
const IndexResults = connectStateResults(
  // eslint-disable-next-line no-unused-vars
  ({ searchState, searchResults, children }) =>
    searchResults && searchResults.nbHits !== 0 ? (
      children
    ) : (
      // <div>
      //   No results have been found for {searchState.query} and index{' '}
      //   {searchResults ? searchResults.index : ''}
      // </div>
      <></>
    )
)

// Algolia custom state result for all indices
const AllResults = connectStateResults(
  ({
    allSearchResults,
    children,
    searchState,
    setTotalHits,
    algolia_index
  }) => {
    const hasResults = allSearchResults && allSearchResults.hits.length !== 0
    if (hasResults) setTotalHits(allSearchResults.hits.length)
    return !hasResults &&
      searchState.query !== '' &&
      searchState.query !== null ? (
      <Layout.Column
        style={{ flex: '1', borderTop: '1px solid #11111122' }}
        center
        padding="T2"
      >
        <Icon size={3} name="no-search" />
        <Heading h3 margin="T2 B2">
          No results for &quot;
          {allSearchResults && allSearchResults.query}&quot;
        </Heading>
        <Index indexName={algolia_index} />
      </Layout.Column>
    ) : (
      children
    )
  }
)

// Algolia custom search box
const SearchBox = React.memo(
  ({ placeholder, currentRefinement, isSearchStalled, refine, setHasQuery, initQuery }) => {
    const [searchString, setSearchStringState] = useState(initQuery.current)
    const isMounted = useIsMounted()

    const setSearchString = useCallback(
      (data) => {
        if (isMounted.current) {
          setSearchStringState(data)
        }
      },
      [setSearchStringState]
    )

    const update = useCallback(
      (query) => {
        setSearchString(query)
      },
      [refine, searchString, setSearchString]
    )

    useEffect(() => {
      setFocus()
      ref.current.value = initQuery.current
      initQuery.current = ''
    }, [])

    useEffect(() => {
      setHasQuery(searchString !== '' && searchString !== null)
      updateQueryNoReload(searchString)
      refine(searchString)
    }, [searchString])

    useEffect(() => {
      if (searchString) refine(searchString)
    }, [refine])
    // eslint-disable-next-line no-unused-vars
    const { ref, _, setFocus } = useFocus()

    return (
      <>
        <CustomRefinementList
          attribute="type"
          query={searchString}
          clearSearch={() => {
            setSearchString('')
            ref.current.value = ''
          }}
        />
        <StyledSearchForm
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            return false
          }}
          role="search"
        >
          <div style={{ flex: '1' }}>
            <StyledSearchInput
              placeholder={placeholder}
              type="search"
              onChange={(event) => {
                update(event.currentTarget.value)
              }}
              ref={ref}
            />
          </div>
          <StyledResetButton
            onClick={() => update('')}
            type="button"
            hidden={currentRefinement === ''}
          >
            <Icon name="close" />
          </StyledResetButton>
          {isSearchStalled ? 'My search is stalled' : ''}
        </StyledSearchForm>
      </>
    )
  }
)

SearchBox.propTypes = {
  placeholder: PropTypes.string,
  currentRefinement: PropTypes.any,
  isSearchStalled: PropTypes.any,
  refine: PropTypes.any,
  inputRef: PropTypes.any,
  initQuery: PropTypes.object,
  setHasQuery: PropTypes.func,
  reConstructArray: PropTypes.func,
  isVisible: PropTypes.bool
}

const CustomSearchBox = connectSearchBox(SearchBox)

const StyledRefinementList = styled.div`
  display: flex;
  position: relative;
  height: 100%;
`

const RefinementItem = styled.div`
  display: flex;
  padding: 0.25rem 0.5rem;
  margin-right: 0.25rem;
  background: #00805d;
  color: white;
  text-transform: capitalize;
  border-radius: 0.25rem;
`

const RefinementItemRemove = styled.div`
  width: 16px;
  min-height: 100%;
  margin-left: 5px;
  display: flex;
  justify-content: flex-end;
  cursor: pointer;
  align-items: center;
  ${Icon} {
    filter: brightness(0) invert(1);
  }
`

const SelectedRefinements = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`

const StyledSuggestion = styled.div`
  position: absolute;
  top: 100%;
  z-index: 500;
  padding: 0.5rem 1rem;
  white-space: nowrap;
  background: #fff;
  border-radius: 0.25rem;
  box-shadow: 0 8px 22px 0 rgb(37 44 97 / 15%),
    0 4px 6px 0 rgb(93 100 148 / 20%);
`

const RefinementList = React.memo(
  ({
    items,
    refine,
    currentRefinement,
    searchForItems,
    query,
    clearSearch
  }) => {
    const timeout = useRef(null)
    const hasRefinement = useRef(false)
    const refinementItem = useRef(null)
    useEffect(() => {
      if (query) {
        if (timeout.current) {
          clearTimeout(timeout.current)
        }
        timeout.current = setTimeout(() => {
          searchForItems(query)
          timeout.current = null
        }, 300)
      }
    }, [query])

    useEffect(() => {
      if (
        items.length === 1 &&
        query.trim().length < items[0].label.length + 1 &&
        query.trim().length > items[0].label.length - 1
      ) {
        hasRefinement.current = true
        refinementItem.current = items[0]
      } else {
        if (hasRefinement.current) {
          hasRefinement.current = false
        }
      }
    }, [items])

    const refineItems = () => {
      if (refinementItem.current) {
        refine([...currentRefinement, ...refinementItem.current.value])
        hasRefinement.current = false
        refinementItem.current = null
        clearSearch()
      }
    }
    const checkKey = (e) => {
      if (e.key === 'Tab') {
        if (hasRefinement.current) {
          e.preventDefault()
          e.stopPropagation()
          refineItems()
        }
      }
    }

    const removeRefinement = (item) => {
      refine(currentRefinement.filter((i) => i !== item))
    }

    const checkRefinement =
      items.length === 1 &&
      query.trim().length < items[0].label.length + 1 &&
      query.trim().length > items[0].label.length - 1 &&
      !currentRefinement.includes(items[0].label)

    useEffect(() => {
      document.addEventListener('keydown', checkKey)
      return () => {
        document.removeEventListener('keydown', checkKey)
      }
    }, [])

    return (
      <StyledRefinementList>
        <SelectedRefinements>
          {currentRefinement.map((item, index) => {
            return (
              <RefinementItem key={`refine-${index}`}>
                {item}
                <RefinementItemRemove onClick={() => removeRefinement(item)}>
                  <Icon size={0.75} name={'close'} />
                </RefinementItemRemove>
              </RefinementItem>
            )
          })}
        </SelectedRefinements>
        {checkRefinement && (
          <StyledSuggestion>
            <Text as={'span'}>
              Filter by{' '}
              <Text as={'span'} bold>
                &quot;{items[0].label}&quot;
              </Text>
              ? Tab to select
            </Text>
          </StyledSuggestion>
        )}
      </StyledRefinementList>
    )
  }
)
RefinementList.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  currentRefinement: PropTypes.array,
  searchForItems: PropTypes.func,
  query: PropTypes.string,
  clearSearch: PropTypes.func
}
const CustomRefinementList = connectRefinementList(RefinementList)

/**
 * Search bar component
 * @param {boolean} nav If search bar is in the nav
 */
const SearchBarComponent = React.memo(
  ({
    nav = false,
    mobile = false,
    margin,
    placeholder = 'What are you looking for?'
  }) => {
    const isWindow = typeof window !== 'undefined'
    const platform = isWindow && window.navigator.platform.toLowerCase()

    const {
      site: {
        siteMetadata: { algolia_index }
      }
    } = useStaticQuery(
      graphql`
        query {
          site {
            siteMetadata {
              algolia_index
            }
          }
        }
      `
    )
    const [hasQuery, setHasQueryState] = useState(false)
    const isMounted = useIsMounted()
    const setHasQuery = useCallback(
      (data) => {
        if (isMounted.current) {
          setHasQueryState(data)
        }
      },
      [setHasQueryState]
    )
    const previewRenderDelay = 150
    // eslint-disable-next-line no-unused-vars
    const mobileView = useMedia('desktop')
    const [cookies, setCookie] = useCookies(['newsletter'])
    const initQuery = useRef(
      new URLSearchParams(
        typeof window !== 'undefined' ? window.location.search : ''
      ).get('q')
    )

    const {
      ref: searchContainer,
      isComponentVisible: isSearchContainerVisible,
      setIsComponentVisible: setSearchContainerVisible
    } = useComponentVisible(false, true)

    const disableNewsletter = (cookie) => {
      switch (cookie) {
        case undefined:
          return true
        case 'false':
          return true
        default:
          return false
      }
    }

    const totalHits = useRef(0)
    const currentSelected = useRef(null)
    const selectedArray = useRef([])
    const selectedIndex = useRef(0)
    const selectedUrl = useRef(null)
    const previewTimeout = useRef(null)
    const renderPreview = useRef(null)

    const checkKey = (e) => {
      e = e || window.event
      if (e.key == 'ArrowUp') {
        e.preventDefault()
        if (selectedIndex.current > 0) {
          if (selectedArray.current[selectedIndex.current - 1] !== null) {
            selectedArray.current[selectedIndex.current - 1](true)
          }
        }
      } else if (e.key == 'ArrowDown') {
        e.preventDefault()
        if (selectedIndex.current < totalHits.current - 1) {
          if (selectedArray.current[selectedIndex.current + 1] !== null) {
            selectedArray.current[selectedIndex.current + 1](true)
          }
        }
      } else if (e.key == 'Enter') {
        e.preventDefault()
        if (selectedUrl.current) {
          navigate(selectedUrl.current)
        }
        return false
      }
    }

    const select = (index, preview) => {
      if (currentSelected.current !== null) {
        currentSelected.current(false)
      }

      selectedIndex.current = index
      selectedUrl.current = preview.data.url
      if (previewTimeout.current) {
        clearTimeout(previewTimeout.current)
      }
      if (renderPreview.current) {
        previewTimeout.current = setTimeout(() => {
          renderPreview.current?.(preview)
          previewTimeout.current = null
        }, previewRenderDelay)
      }
      currentSelected.current = selectedArray.current[index]
    }

    const passSetSelected = (func, index) => {
      selectedArray.current[index] = func
      if (index === 0 && selectedArray.current[index] !== null) {
        selectedArray.current[index](true)
      }
    }

    const setTotalHits = (total) => {
      totalHits.current = total
    }

    const onPreviewRender = useCallback((render) => {
      renderPreview.current = render
    })

    const openViaKeyboard = useCallback((e) => {
      if (e.key === 'k' && e.metaKey) {
        setSearchContainerVisible(true)
      }
    })

    useEffect(() => {
      if (initQuery.current) {
        setSearchContainerVisible(true)
      }
      const listener = document.addEventListener('keydown', openViaKeyboard)
      return () => {
        document.removeEventListener('keydown', listener)
      }
    }, [])

    useEffect(() => {
      if (!isSearchContainerVisible) {
        updateQueryNoReload('')
      }
    }, [isSearchContainerVisible])
    return (
      <>
        {nav ? (
          <SearchNavButton
            onClick={() => {
              setSearchContainerVisible(true)
              if (disableNewsletter(cookies.newsletter)) {
                setCookie('newsletter', 'pause')
              }
            }}
            mobile={mobile}
          >
            <Icon size={1.5} name={'search-white'} style={{opacity: 0.8}} />
            {!mobile && (
              <>
                <Text lineHeight="1.5" size="0.9" color="rgba(255, 255, 255, 0.6)" as="span" margin="L0.5 R4">
                  {placeholder}
                </Text>
                <HotkeyContainer>
                  <StyledHotkey>
                    {isWindow && platform.includes('mac') ? `CMD` : `CTRL`}
                  </StyledHotkey>
                  <StyledHotkey>K</StyledHotkey>
                </HotkeyContainer>
              </>
            )}
          </SearchNavButton>
        ) : (
          <SearchBarButton
            margin={margin}
            onClick={() => {
              setSearchContainerVisible(true)
              if (disableNewsletter(cookies.newsletter)) {
                setCookie('newsletter', 'pause')
              }
            }}
          >
            <Icon size={1.5} name="search-white" />
            <Text
              as="span"
              size={1.2}
              margin="L1"
              style={{ flex: '1', color: 'white' }}
            >
              {placeholder}
            </Text>
            <StyledButton as="a">Search</StyledButton>
          </SearchBarButton>
        )}
        {isSearchContainerVisible && (
          <InstantSearch searchClient={searchClient} indexName={algolia_index}>
            <Configure hitsPerPage={20} facets={['type']} />

            <SearchContainerOverlay>
              <SearchContainer
                ref={searchContainer}
                hasQuery={hasQuery}
                onKeyDown={checkKey}
              >
                <SearchHeader>
                  <Icon margin="L1 R1" size={2} name="search" />
                  <CustomSearchBox
                    setHasQuery={setHasQuery}
                    setVisible={setSearchContainerVisible}
                    initQuery={initQuery}
                    placeholder={placeholder}
                  />
                  <TextButton
                    onClick={() => {
                      setSearchContainerVisible(false)
                    }}
                  >
                    Cancel
                  </TextButton>
                </SearchHeader>
                <AllResults
                  hasQuery={hasQuery}
                  setTotalHits={setTotalHits}
                  algolia_index={algolia_index}
                >
                  <Layout.Row
                    style={{
                      display: !hasQuery ? 'none' : undefined,
                      flexGrow: '1',
                      borderTop: '1px solid #11111122',
                      overflow: 'hidden'
                    }}
                    justify="flex-start"
                  >
                    <SearchResultBox>
                      <div>
                        <section
                          aria-label="Search Results"
                          aria-busy="false"
                          aria-live="polite"
                        >
                          <Layout.Column
                            full
                            style={{ flex: 'none', alignItems: 'stretch' }}
                          >
                            <Index indexName={algolia_index}>
                              <IndexResults>
                                <Hits
                                  select={select}
                                  selectedUrl={selectedUrl}
                                  passSetSelected={passSetSelected}
                                />
                              </IndexResults>
                            </Index>
                          </Layout.Column>
                        </section>
                      </div>
                    </SearchResultBox>
                    {!mobileView && (
                      <PreviewBox
                        style={{
                          width: '65%',
                          overflowY: 'auto',
                          height: '100%'
                        }}
                        onPreviewRender={onPreviewRender}
                      ></PreviewBox>
                    )}
                  </Layout.Row>
                </AllResults>
                <SearchFooter
                  style={{ display: !hasQuery ? 'none' : undefined }}
                ></SearchFooter>
              </SearchContainer>
            </SearchContainerOverlay>
          </InstantSearch>
        )}
      </>
    )
  }
)

SearchBarComponent.propTypes = {
  nav: PropTypes.bool,
  mobile: PropTypes.bool,
  margin: PropTypes.string,
  placeholder: PropTypes.string
}

const SearchBar = styled(SearchBarComponent)`
  display: flex;
`

export default SearchBar
